import React, { useMemo } from 'react';
import { Box, Grid, Typography, Chip, Stack, IconButton, Tooltip, CircularProgress, Alert } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import EqpMonitorCard from './EqpMonitorCard';
import { EQP_STATE_ORDER, EQP_STATE_LABEL, getEqpStateChipColor } from './eqpState';

// 헤더(상태별 카운트 칩 + 마지막 갱신 시각) + 카드 그리드. 칩을 누르면 그 상태만 걸러 본다.
const EqpMonitorBoard = ({ list, loading, lastRefresh, stateFilter, onStateFilterChange, onCardClick, onRefresh }) => {
  const counts = useMemo(() => {
    const c = { ALL: list.length, NODATA: 0 };
    EQP_STATE_ORDER.forEach((s) => { c[s] = 0; });
    list.forEach((row) => {
      if (row.stale || !row.runSt) { c.NODATA += 1; return; }
      if (c[row.runSt] !== undefined) c[row.runSt] += 1;
    });
    return c;
  }, [list]);

  const filtered = useMemo(() => {
    if (!stateFilter) return list;
    if (stateFilter === 'NODATA') return list.filter((r) => r.stale || !r.runSt);
    return list.filter((r) => !r.stale && r.runSt === stateFilter);
  }, [list, stateFilter]);

  const monitored = list.length > 0;
  const toggle = (key) => onStateFilterChange(stateFilter === key ? null : key);

  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ mb: 2, rowGap: 1 }}>
        <Chip
          label={`전체 ${counts.ALL}`}
          color={!stateFilter ? 'primary' : 'default'}
          variant={!stateFilter ? 'filled' : 'outlined'}
          onClick={() => onStateFilterChange(null)}
        />
        {EQP_STATE_ORDER.map((s) => (
          <Chip
            key={s}
            label={`${EQP_STATE_LABEL[s]} ${counts[s]}`}
            color={stateFilter === s ? getEqpStateChipColor(s) : 'default'}
            variant={stateFilter === s ? 'filled' : 'outlined'}
            onClick={() => toggle(s)}
          />
        ))}
        <Chip
          label={`데이터 없음 ${counts.NODATA}`}
          variant={stateFilter === 'NODATA' ? 'filled' : 'outlined'}
          onClick={() => toggle('NODATA')}
        />

        <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography variant="caption" color="text.secondary">
            {lastRefresh ? `${lastRefresh.format('HH:mm:ss')} 갱신` : ''}
          </Typography>
          <Tooltip title="새로고침">
            <IconButton size="small" onClick={onRefresh}><RefreshIcon fontSize="small" /></IconButton>
          </Tooltip>
        </Box>
      </Stack>

      {loading && list.length === 0 ? (
        <Box sx={{ py: 6, textAlign: 'center' }}><CircularProgress size={28} /></Box>
      ) : !monitored ? (
        <Alert severity="info">
          모니터링 중인 장비가 없습니다. 장비관리에서 장비별로 모니터링을 켜 주세요.
        </Alert>
      ) : filtered.length === 0 ? (
        <Alert severity="info">해당 상태의 장비가 없습니다.</Alert>
      ) : (
        <Grid container spacing={2}>
          {filtered.map((row) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={row.eqpUuid}>
              <EqpMonitorCard row={row} onClick={() => onCardClick(row)} />
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
};

export default EqpMonitorBoard;
